"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import Link from "next/link";
import { CTAButton, SectionHeader } from "fansunited-sports-ui";
import type { FUSportsSquadMember } from "fansunited-data-layer";
import { useTeamPageData } from "../context/TeamPageDataContext";
import { getFeaturedPlayersByPosition, type PositionGroup } from "@/utilities/mappers/player";
import { shirtNumberSvg } from "@/utilities/mappers/squad";
import { getPositionGroup } from "@/utilities/mappers/squad";
import { toSquadMembers } from "../utils/toSquadMembers";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
interface TeamFeaturedPlayersProps {
  onViewAll?: () => void;
}
interface Spotlight {
  group: PositionGroup;
  role: string; // headline (e.g. "LEADING SCORER")
  stat: {
    label: string;
    value: number | string;
  }; // the hero stat
  supporting: {
    label: string;
    value: number | string;
  }[];
  member: FUSportsSquadMember;
}
export const TeamFeaturedPlayers: React.FC<TeamFeaturedPlayersProps> = t0 => {
  const $ = _c(41);
  const {
    onViewAll
  } = t0;
  const {
    team,
    athleteStats
  } = useTeamPageData();
  const labels = useLabels().teamPage;
  const nav = useNavigation();
  const {
    allowPlayerPhotos,
    namedColors
  } = useTenantSettings();
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== allowPlayerPhotos || $[1] !== athleteStats || $[2] !== labels || $[3] !== namedColors || $[4] !== nav || $[5] !== onViewAll || $[6] !== team) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const primaryColor = namedColors.find(_temp)?.value;
      const primaryForeground = namedColors.find(_temp2)?.value;
      let t5;
      if ($[11] !== athleteStats || $[12] !== labels || $[13] !== team.squad) {
        bb1: {
          const players = team.squad ?? [];
          if (players.length === 0) {
            let t6;
            if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
              t6 = [];
              $[15] = t6;
            } else {
              t6 = $[15];
            }
            t5 = t6;
            break bb1;
          }
          const members = toSquadMembers(players, athleteStats);
          const picks = getFeaturedPlayersByPosition(members);
          const abbr = labels.playerCardStatsAbbr;
          const roles = labels.featuredPlayers.roles;
          let t6;
          if ($[16] !== abbr || $[17] !== labels.featuredPlayers.signatureStat || $[18] !== roles) {
            t6 = member => {
              const group = getPositionGroup(member.position) as PositionGroup;
              const s = member.seasonStatistics;
              const apps = s?.appearances ?? 0;
              const subs = s?.substituteIn ?? 0;
              const mins = s?.minutes ?? 0;
              const goals = s?.goals ?? 0;
              const assists = s?.assists ?? 0;
              const cleanSheets = s?.cleansheets ?? 0;
              s?.conceded ?? 0;
              if (group === "DEF") {
                return {
                  group,
                  role: roles.def,
                  stat: {
                    label: labels.featuredPlayers.signatureStat.cleanSheets,
                    value: cleanSheets
                  },
                  supporting: [{
                    label: abbr.g,
                    value: goals
                  }, {
                    label: abbr.a,
                    value: assists
                  }, {
                    label: abbr.app,
                    value: `${apps} (${subs})`
                  }, {
                    label: abbr.min,
                    value: mins
                  }],
                  member
                };
              }
              if (group === "MID") {
                return {
                  group,
                  role: roles.mid,
                  stat: {
                    label: labels.featuredPlayers.signatureStat.goalsPlusAssists,
                    value: goals + assists
                  },
                  supporting: [{
                    label: abbr.g,
                    value: goals
                  }, {
                    label: abbr.a,
                    value: assists
                  }, {
                    label: abbr.app,
                    value: `${apps} (${subs})`
                  }, {
                    label: abbr.min,
                    value: mins
                  }],
                  member
                };
              }
              return {
                group: "FWD",
                role: roles.fwd,
                stat: {
                  label: labels.featuredPlayers.signatureStat.goals,
                  value: goals
                },
                supporting: [{
                  label: abbr.a,
                  value: assists
                }, {
                  label: abbr.app,
                  value: `${apps} (${subs})`
                }, {
                  label: abbr.min,
                  value: mins
                }, ...(goals > 0 ? [{
                  label: abbr.mPerG,
                  value: Math.round(mins / goals * 10) / 10
                }] : [])].slice(0, 4),
                member
              };
            };
            $[16] = abbr;
            $[17] = labels.featuredPlayers.signatureStat;
            $[18] = roles;
            $[19] = t6;
          } else {
            t6 = $[19];
          }
          t5 = picks.map(t6);
        }
        $[11] = athleteStats;
        $[12] = labels;
        $[13] = team.squad;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      const spotlights = t5;
      if (spotlights.length === 0) {
        t4 = null;
        break bb0;
      }
      const nativeTeamId = team.id;
      let t6;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          typography: {
            name: "text-sm uppercase font-semibold text-foreground"
          }
        };
        $[20] = t6;
      } else {
        t6 = $[20];
      }
      let t7;
      if ($[21] !== labels.featuredPlayers.title) {
        t7 = {
          name: labels.featuredPlayers.title,
          styles: t6
        };
        $[21] = labels.featuredPlayers.title;
        $[22] = t7;
      } else {
        t7 = $[22];
      }
      let t8;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          container: "mb-2"
        };
        $[23] = t8;
      } else {
        t8 = $[23];
      }
      let t9;
      if ($[24] !== nativeTeamId || $[25] !== nav || $[26] !== onViewAll) {
        t9 = onViewAll ? {
          onClick: onViewAll
        } : {
          href: `${nav.team(nativeTeamId)}#squad`
        };
        $[24] = nativeTeamId;
        $[25] = nav;
        $[26] = onViewAll;
        $[27] = t9;
      } else {
        t9 = $[27];
      }
      let t10;
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = {
          container: "text-sm hover:underline border border-transparent p-0 m-0 cursor-pointer"
        };
        $[28] = t10;
      } else {
        t10 = $[28];
      }
      let t11;
      if ($[29] !== labels.featuredPlayers.viewAll || $[30] !== t9) {
        t11 = <CTAButton text={labels.featuredPlayers.viewAll} {...t9} variant="custom" styles={t10} />;
        $[29] = labels.featuredPlayers.viewAll;
        $[30] = t9;
        $[31] = t11;
      } else {
        t11 = $[31];
      }
      if ($[32] !== t11 || $[33] !== t7) {
        t3 = <SectionHeader entity={t7} styles={t8} primaryCta={t11} />;
        $[32] = t11;
        $[33] = t7;
        $[34] = t3;
      } else {
        t3 = $[34];
      }
      t1 = "grid grid-cols-1 md:grid-cols-3 gap-4";
      t2 = spotlights.map(t12 => {
        const {
          group: group_0,
          role,
          stat,
          supporting,
          member: member_0
        } = t12;
        const photo = allowPlayerPhotos ? member_0.assets?.photo || member_0.assets?.photoTransparent : null;
        const flag = member_0.country?.flag;
        const code = member_0.country?.code;
        const shirt = member_0.shirtNumber;
        return <Link key={member_0.id} href={nav.player(member_0.id)} prefetch={false} className="group relative overflow-hidden rounded-lg bg-card shadow-md hover:shadow-lg transition-shadow"><div className="relative py-6 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent flex items-center justify-center"><div className="h-28 w-28 rounded-full overflow-hidden ring-4 ring-background shadow-lg bg-muted">{photo ? <img src={photo} alt={member_0.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" /> : <img src={shirtNumberSvg(shirt ?? "\u2013", primaryColor, primaryForeground)} alt="" className="h-full w-full object-cover" />}</div><div className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wide"><span className="rounded bg-foreground/80 text-background px-1.5 py-0.5">{labels.positionsAbbr[group_0.toLowerCase() as "def" | "mid" | "fwd"]}</span>{shirt != null && <span className="rounded bg-foreground/20 text-foreground px-1.5 py-0.5 tabular-nums">#{shirt}</span>}</div></div><div className="p-4 space-y-3"><div className="flex items-baseline gap-2 min-w-0"><h3 className="font-semibold text-foreground truncate">{member_0.name}</h3>{flag && <img src={flag} alt={code ?? ""} className="h-3 w-5 rounded-sm flex-shrink-0" loading="lazy" />}</div><div className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">{role}</div><div className="flex items-baseline gap-2"><span className="text-4xl font-black tabular-nums text-foreground leading-none">{stat.value}</span><span className="text-sm text-muted-foreground">{stat.label}</span></div><div className="flex items-center gap-3 pt-2 border-t border-border/50 text-xs">{supporting.map(_temp3)}</div></div></Link>;
      });
    }
    $[0] = allowPlayerPhotos;
    $[1] = athleteStats;
    $[2] = labels;
    $[3] = namedColors;
    $[4] = nav;
    $[5] = onViewAll;
    $[6] = team;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[35] !== t1 || $[36] !== t2) {
    t5 = <div className={t1}>{t2}</div>;
    $[35] = t1;
    $[36] = t2;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  let t6;
  if ($[38] !== t3 || $[39] !== t5) {
    t6 = <div>{t3}{t5}</div>;
    $[38] = t3;
    $[39] = t5;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  return t6;
};
function _temp(c) {
  return c.name === "primary";
}
function _temp2(c_0) {
  return c_0.name === "primaryForeground";
}
function _temp3(s_0) {
  return <div key={s_0.label} className="flex items-baseline gap-1"><span className="font-semibold tabular-nums text-foreground">{s_0.value}</span><span className="text-muted-foreground">{s_0.label}</span></div>;
}